import type { EpisodeLine } from "./stage-api";
import { validatedVisuals, visualAssetUrl, type EditionVisualCue } from "./edition-visuals.ts";

export type VisualAsset = { url: string; hash: string; video: boolean };
export const MAX_VISUAL_ASSET_BYTES = 25_000_000;

/** Current/next cue only, including the next turn. Never fetch a stale track. */
export function visualPreloadWindow(lines: readonly EpisodeLine[], line: EpisodeLine | undefined,
  elapsedMs: number, video: boolean, lookAhead = true): VisualAsset[] {
  const start = line ? lines.indexOf(line) : 0;
  if (start < 0 || !Number.isFinite(elapsedMs)) return [];
  const cues: EditionVisualCue[] = [];
  for (let i = start; i < lines.length && cues.length < (lookAhead ? 2 : 1); i++) {
    for (const cue of validatedVisuals(lines[i])) {
      if (i === start && cue.untilMs <= elapsedMs) continue;
      if (!lookAhead && (i !== start || cue.atMs > elapsedMs)) continue;
      cues.push(cue);
      if (cues.length === (lookAhead ? 2 : 1)) break;
    }
    if (!lookAhead) break;
  }
  const assets: VisualAsset[] = [];
  for (const cue of cues) {
    assets.push({ url: cue.kind === "video" ? cue.posterUrl! : cue.url,
      hash: cue.kind === "video" ? cue.posterSha256! : cue.sha256, video: false });
    if (cue.kind === "video" && video) assets.push({ url: cue.url, hash: cue.sha256, video: true });
  }
  return assets.filter((asset, i) => assets.findIndex(a => a.url === asset.url) === i);
}

type Entry = { asset: VisualAsset; controller: AbortController; state: "queued" | "loading" | "ready" | "failed"; objectUrl?: string };
type Dependencies = { fetch: typeof fetch; createUrl: (blob: Blob) => string; revokeUrl: (url: string) => void; timeoutMs: number };

/** At most four assets, two concurrent requests and 25 MB per asset.
 * Completed blobs are reused by the visible media element, including video seeks.
 * Leaving the window aborts requests and releases object URLs; failures never retry
 * inside the same window or hold up the speech clock. */
export class EditionVisualPreloader {
  private entries = new Map<string, Entry>();
  private running = 0;
  private changed: () => void;
  private deps: Dependencies;
  constructor(changed: () => void, deps: Dependencies = {
    fetch: (...args) => fetch(...args), createUrl: blob => URL.createObjectURL(blob),
    revokeUrl: url => URL.revokeObjectURL(url), timeoutMs: 10_000,
  }) { this.changed = changed; this.deps = deps; }

  source(url: string): string | undefined { return this.entries.get(url)?.objectUrl; }
  failed(url: string): boolean { return this.entries.get(url)?.state === "failed"; }

  reconcile(assets: readonly VisualAsset[]) {
    const desired = assets.filter(a => visualAssetUrl(a.url, a.hash, a.video)).slice(0, 4);
    for (const [url, entry] of this.entries) {
      if (desired.some(a => a.url === url)) continue;
      this.entries.delete(url);
      entry.controller.abort();
      if (entry.objectUrl) this.deps.revokeUrl(entry.objectUrl);
    }
    for (const asset of desired) if (!this.entries.has(asset.url))
      this.entries.set(asset.url, { asset, controller: new AbortController(), state: "queued" });
    this.pump();
  }
  clear() { this.reconcile([]); }

  private pump() {
    for (const entry of this.entries.values()) {
      if (this.running >= 2) break;
      if (entry.state !== "queued") continue;
      entry.state = "loading";
      this.running++;
      void this.load(entry).finally(() => { this.running--; this.pump(); });
    }
  }
  private async load(entry: Entry) {
    const timer = setTimeout(() => entry.controller.abort(), this.deps.timeoutMs);
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    try {
      const response = await this.deps.fetch(entry.asset.url, { signal: entry.controller.signal, credentials: "same-origin", cache: "force-cache", redirect: "error" });
      if (!response.ok || !response.body || Number(response.headers.get("content-length")) > MAX_VISUAL_ASSET_BYTES)
        throw new Error("Visual response unavailable or oversized");
      reader = response.body.getReader();
      const chunks: Uint8Array<ArrayBuffer>[] = [];
      let size = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > MAX_VISUAL_ASSET_BYTES || entry.controller.signal.aborted) throw new Error("Visual response limit");
        chunks.push(new Uint8Array(value));
      }
      if (!size) throw new Error("Empty visual response");
      if (entry.controller.signal.aborted || this.entries.get(entry.asset.url) !== entry) return;
      const type = entry.asset.video ? "video/mp4" : entry.asset.url.endsWith(".jpg") ? "image/jpeg" : entry.asset.url.endsWith(".png") ? "image/png" : "image/webp";
      entry.objectUrl = this.deps.createUrl(new Blob(chunks, { type }));
      entry.state = "ready";
      this.changed();
    } catch {
      if (this.entries.get(entry.asset.url) === entry) { entry.state = "failed"; this.changed(); }
    } finally {
      clearTimeout(timer);
      await reader?.cancel().catch(() => {});
    }
  }
}
